import { constants } from '@yola/subscription-manager-js';
import {
  getPricePerYear,
  getPricePerMonth,
  getDomainPricePerYear,
} from '../../common/helpers/get-price-data';
import { t } from '../../localization';
import yoproductsServiceClient from '../../common/helpers/yoproducts-service-client';
import configStorage from '../../configuration/config-storage';
import getDomainParts from '../../common/helpers/get-domains-parts';

const { B2C_HOSTING_FREE, B2C_HOSTING_BRONZE, B2C_HOSTING_SILVER, B2C_HOSTING_GOLD } =
  constants.SubscriptionType;

const getCollapseCaptions = () => ({
  collapseOpenText: t('Hide features'),
  collapseHiddenText: t('Show features'),
});

const getFooterCaptions = ({ moneyBackGuarantee, partnerName }) => ({
  paymentsTitle: t('Accepted payments methods'),
  moneyBackTitle: !moneyBackGuarantee
    ? null
    : t('{days}-day money back guarantee', {
        days: moneyBackGuarantee,
      }),
  moneyBackDescription: !moneyBackGuarantee
    ? null
    : t('Try {partnerName} for {days} days and if you’re not 100% satisfied, get your money back', {
        days: moneyBackGuarantee,
        partnerName,
      }),
  securityTitle: t('SSL secure payment'),
  securityDescription: t('Your information is protected by 256-bit SSL encryption'),
});

const getPlanDescription = (plan) => {
  const description = {
    [B2C_HOSTING_FREE]: t('Start for free'),
    [B2C_HOSTING_BRONZE]: t('Personal use'),
    [B2C_HOSTING_SILVER]: t('Entrepreneurs & Freelancers'),
    [B2C_HOSTING_GOLD]: t('Grow your business'),
  };

  return description[plan] || null;
};

const getLabelCaptions = () => ({
  popular: t('Most popular'),
  current: t('Current plan'),
  valuable: t('Most valuable'),
  limited: t('Limited offer'),
});

const getDiscount = (subscriptions, currency, prices) => {
  const discounts = [];

  subscriptions.forEach((subscription) => {
    const product = prices[subscription];
    if (!product) return;

    const annual = getPricePerYear({ prices, plan: subscription, currency });
    const month = getPricePerMonth({ prices, plan: subscription, currency });

    const discount = Math.round(((12 * month - annual) * 100) / (12 * month));

    discounts.push(discount);
  });

  return Math.max(...discounts);
};

const getExtraPriceInfoTooltip = (discount) => {
  const firstPart = t('Limited time offer! Get');
  const secondPart = t('off your first purchase');
  return `${firstPart} ${discount}% ${secondPart}`;
};

const getDomainPromoCaptions = ({
  currency,
  prices,
  availablePlanIds,
  domainName,
  domainDiscount,
  isTrialDomainDiscountAvailable,
}) => {
  if (domainName && domainDiscount) {
    const yoproducts = yoproductsServiceClient.get();
    const { locale = 'en' } = configStorage.get();
    const { tldParts } = getDomainParts(domainName);

    const params = { prices, tld: tldParts.join('.'), currency };

    const domainPrice = getDomainPricePerYear(params);
    const discountedDomainPrice = getDomainPricePerYear(params, 'discounted');

    const discount = domainPrice - discountedDomainPrice;
    const isFreeDomain = discount >= domainPrice;
    const formattedDiscount = yoproducts.actions.formatPrice(discount, locale, currency, {
      leadingCurrency: true,
    });
    const freeDomainText = t('Get <b>{domainName}</b> for free with an annual plan', {
      domainName,
    });

    if (isTrialDomainDiscountAvailable) {
      return {
        text: isFreeDomain
          ? freeDomainText
          : t('Save {discount} on your <b>{domainName}</b> with annual plans', {
              domainName,
              discount: formattedDiscount,
            }),
      };
    }

    return {
      text: isFreeDomain
        ? freeDomainText
        : t('Save up to {discount} on <b>{domainName}</b> with annual plans', {
            domainName,
            discount: formattedDiscount,
          }),
    };
  }

  const discountValue = getDiscount(availablePlanIds, currency, prices);
  const discount = Number.isInteger(discountValue) ? `${discountValue}` : 'X';

  if (isTrialDomainDiscountAvailable) {
    return {
      text: t('Save up to <b>{discount}%</b> with annual plans', { discount }),
    };
  }

  return {
    text: t('<i>Save up to <b>{discount}%</b> and get a <b>free</b> domain for 1 year</i>', {
      discount,
    }),
    promoText: t(
      'Get a free domain for 1 year. Offer valid for the following extensions: <b>.com, .uk, .co.uk, .us, .it, .biz, .de, .nl, .eu, .in, .co.za, .club, .info, .work, .xyz</b><br/>OR get a discount for any other extension'
    ),
  };
};

const getAnnualDiscountCaptions = (currency, prices, availablePlanIds) => {
  const discountValue = getDiscount(availablePlanIds, currency, prices);
  const discount = Number.isInteger(discountValue) ? `${discountValue}` : 'X';

  return {
    text: t('Save up to {discount}% with annual plans', { discount }),
  };
};

export {
  getCollapseCaptions,
  getFooterCaptions,
  getPlanDescription,
  getLabelCaptions,
  getDomainPromoCaptions,
  getAnnualDiscountCaptions,
  getExtraPriceInfoTooltip,
};
